import 'rxjs/add/observable/throw';
import { Injectable } from '@angular/core';
import { Http, Response, RequestOptionsArgs, Headers, RequestOptions } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import * as moment from 'moment';
import { environment } from './../../environments/environment';
import { NbAuthService, NbAuthJWTToken } from '@nebular/auth';

@Injectable()
export class ZHttpService {
    protected baseUrl: string;

    constructor(protected http: Http, protected authService: NbAuthService) { }

    /**
     * Proxy GET to HTTP service
     */
    rawGet<T>(url: string, options?: RequestOptionsArgs): Observable<T> {
        const exception = this.checkOnline();

        if (exception) {
            return this.handleRawResponse<T>(exception);
        } else {
            return this.handleRawResponse<T>(this.http.get(url, options));
        }
    }

    /**
     * Proxy POST to HTTP service
     */
    rawPost<T>(url: string, body: any, options?: RequestOptionsArgs): Observable<T> {
        const exception = this.checkOnline();

        if (exception) {
            return this.handleRawResponse<T>(exception);
        } else {
            return this.handleRawResponse<T>(this.http.post(url, body, options));
        }
    }

    /**
     * Proxy PUT to HTTP service
     */
    rawPut<T>(url: string, body: any, options?: RequestOptionsArgs): Observable<T> {
        const exception = this.checkOnline();

        if (exception) {
            return this.handleRawResponse<T>(exception);
        } else {
            return this.handleRawResponse<T>(this.http.put(url, body, options));
        }
    }

    /**
     * Proxy DELETE to HTTP service
     */
    rawDelete<T>(url: string, options?: RequestOptionsArgs): Observable<T> {
        const exception = this.checkOnline();

        if (exception) {
            return this.handleRawResponse<T>(exception);
        } else {
            return this.handleRawResponse<T>(this.http.delete(url, options));
        }
    }

    /**
     * Authorized GET request (with Bareer header filled).
     */
    authGet<T>(url: string): Observable<T> {
        const action = (accessToken: string) => {
            let headerItems = this.getHeaderItems(accessToken);

            headerItems = Object.assign({}, headerItems, {
                'Accept': 'application/json'
            });

            const headers = new Headers(headerItems);
            const options = new RequestOptions({ headers: headers });

            return this.rawGet<T>(this.parseUrl(url), options);
        };

        return this.request(action);
    }

    /**
     * Authorized POST request (with Bareer header filled).
     */
    authPost<T>(url: string, body: any): Observable<number> {
        const action = () => {
            return this.getAccessToken()
                .switchMap(accessToken => {
                    let headerItems = this.getHeaderItems(accessToken);

                    headerItems = Object.assign({}, headerItems, {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    });

                    const headers = new Headers(headerItems);
                    const options = new RequestOptions({ headers: headers });

                    return this.rawPost<number>(this.parseUrl(url), body, options);
                });
        };

        return this.request(action);
    }

    /**
     * Authorized POST request (with Bareer header filled).
     */
    authPut<T>(url: string, body: any): Observable<T> {
        const action = () => {
            return this.getAccessToken()
                .switchMap(accessToken => {
                    let headerItems = this.getHeaderItems(accessToken);

                    headerItems = Object.assign({}, headerItems, {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    });

                    const headers = new Headers(headerItems);
                    const options = new RequestOptions({ headers: headers });

                    return this.rawPut<T>(this.parseUrl(url), body, options);
                });
        };

        return this.request(action);
    }

    /**
     * Authorized POST request (with Bareer header filled).
     */
    authDelete(url: string): Observable<boolean> {
        const action = () => {
            return this.getAccessToken()
                .switchMap(accessToken => {
                    let headerItems = this.getHeaderItems(accessToken);

                    headerItems = Object.assign({}, headerItems, {
                        'Accept': 'application/json'
                    });

                    const headers = new Headers(headerItems);
                    const options = new RequestOptions({ headers: headers });

                    return this.rawDelete<boolean>(this.parseUrl(url), options);
                });
        };

        return this.request(action);
    }

    getAccessToken(): Observable<string> {

        return this.authService.getToken()
            .switchMap((token: NbAuthJWTToken) => {
                if (token) {
                    return Observable.of(token.getValue());
                } else {
                    return Observable.of(null)
                }
            });
    }

    isOnline() {
        return navigator && navigator.onLine !== undefined && navigator.onLine;
    }

    /**
     * Converts to generic object
     */
    private handleRawResponse<T>(response$: Observable<Response>): Observable<any> {
        return response$
            .map((response: Response) => {
                const res = this.extractJsonData<ResponseData>(response, true);
                if (!res.success) {
                    throw response;
                }
                return <T>res.data;
            })
            .catch(error => this.handleError(error));
    }

    private handleError(error: any, params?: any): Observable<any> {
        // TODO: handle error
        if (error instanceof Response && error.text()[0] === '{') {
            error = <ResponseData>this.deserialize(error.text());
        }
        return Observable.throw(Observable.of(error));
    }

    private parseUrl(url: string): string {
        if (url && url[0] !== '/') {
            url = '/' + url;
        }
        return environment.apiUrl + url;
    }

    private extractJsonData<T>(res: Response, convertSnakeToCamel: boolean = false): T {
        const contentType = res.headers.get('content-type');

        if (contentType && contentType.indexOf('application/json') !== -1) {
            const obj = <T>this.deserialize(res.text());
            if (convertSnakeToCamel) {
                return this.snakeToCamelCase(obj);
            }
            return obj;
        }

        return null;
    }

    private snakeToCamelCase(obj) {
        let output, i = 0, l = 0;

        if (!(obj === Object(obj))) {
            return obj;
        }

        if (obj instanceof Array) {
            output = [];
            for (l = obj.length; i < l; i++) {
                output.push(this.snakeToCamelCase(obj[i]));
            }
        } else {
            output = {};
            for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    if (obj[key] instanceof Date) {
                        output[key] = obj[key];
                    } else {
                        output[this.camelize(key)] = this.snakeToCamelCase(obj[key]);
                    }
                }
            }
        }
        return output;
    };

    private camelize(string) {
        if (!isNaN(string)) {
            return string;
        }
        string = string.replace(/[\-_\s]+(.)?/g, function (match, chr) {
            return chr ? chr.toUpperCase() : '';
        });
        // Ensure 1st char is always lowercase
        return string.substr(0, 1).toLowerCase() + string.substr(1);
    };

    private deserialize(data: string): any {
        return JSON.parse(data, (key: any, value: any) => this.reviveDateTime(key, value));
    }

    private getHeaderItems(accessToken: string) {
        return {
            'Authorization': 'Bearer ' + accessToken
        };
    }


    private reviveDateTime(key: any, value: any): any {
        if (typeof value === 'string') {

            key = String(key).toLowerCase();
            const isDate = key.indexOf('time') !== -1 || key.indexOf('date') !== -1;

            const a = /\/Date\((\d*)\)\//.exec(value);
            if (a) {
                return this.normalizeToLocal(new Date(+a[1]));
            } else if (isDate && this.isValid(value)) {
                return this.normalizeToLocal(new Date(value));
            }
        }

        return value;
    }

    private normalizeToLocal(date: Date): Date {
        return moment.utc(date).toDate();
    }

    private isValid(dateString: string): boolean {
        // For new formats support we can extend
        const supportedFormats: Array<string> = [
            'YYYY-MM-DD',
            'YYYY-MM-DDTHH:mm',
            'YYYY-MM-DDTHH:mm:ss',
            'YYYY-MM-DDTHH:mm:ss.SSSZZ'
        ];

        return moment(dateString, supportedFormats, true).isValid();
    }


    private request<T>(requestAction: (accessToken: string) => Observable<T>): Observable<T> {

        let isRetry = true;

        const exception = this.checkOnline();

        if (exception) {
            return Observable.throw(exception);
        }

        const action = () => {
            return this.getAccessToken()
                .switchMap(accessToken => {
                    return requestAction(accessToken)
                        .catch((err) => {
                            isRetry = isRetry && !err.value.ok
                            if (isRetry) {
                                isRetry = false;
                                return action();
                            } else {
                                throw err;
                            }
                        });
                });
        };

        return action();
    }

    private checkOnline() {
        if (!this.isOnline()) {
            const exception = new Error('No internet connection');
            return Observable.throw(exception);
        }
        return null;
    }

}


class ResponseData {
    data: any;
    message: string;
    success: boolean;
}



// WEBPACK FOOTER //
// ./src/app/services/http.service.ts